/** Bellek şablonları: dizi, eşleştirme, kodlama, işitsel yönerge, okuma */

import { Rng } from '../../prng'
import {
  LETTERS,
  SHAPES_HOLLOW,
  SHAPES_SIMPLE,
  WORDS_TR,
  gradeList,
  gradeSet,
  gradeSingle,
  isaret,
} from '../helpers'
import { SEKIL_ADI } from '../../render/shapes'
import type { ShapeName } from '../../render/shapes'
import { EXERCISE_VERSION, clampLevel, scale } from '../types'
import type { CellNode, ExerciseTemplate } from '../types'

/* ================================================================== *
 * 6. Dizi hatırlama
 *
 * Diziyi gör, kapat, geri yaz. Yüksek seviyede TERSTEN istenir —
 * düz dizi depolamayı, ters dizi işleyen belleği ölçer; ikisi farklı
 * şeylerdir ve program ilerledikçe ikincisine geçilmeli.
 * ================================================================== */
export const spanRecall: ExerciseTemplate = {
  code: 'span_recall',
  title: 'Diziyi hatırla',
  domains: ['working_memory_verbal', 'working_memory_visual'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      length: scale(l, 3, 9),
      rounds: 5,
      reversed: l >= 6,
      symbols: l >= 4 ? 'shape' : 'digit',
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const len = p['length'] as number
    const rounds = p['rounds'] as number
    const useShapes = p['symbols'] === 'shape'
    const reversed = p['reversed'] as boolean

    const sequences: string[][] = []
    for (let r = 0; r < rounds; r++) {
      const seq = Array.from({ length: len }, () =>
        useShapes ? rng.pick(SHAPES_SIMPLE) : String(rng.int(1, 9)),
      )
      sequences.push(seq)
    }

    const cells: CellNode[] = []
    for (const seq of sequences) {
      for (const s of seq) cells.push(isaret(s))
      cells.push({})
    }

    return {
      code: 'span_recall',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: reversed
        ? 'Diziyi oku, kapat ve TERSTEN yaz.'
        : 'Diziyi oku, kapat ve aynı sırayla yaz.',
      spokenPrompt: reversed
        ? 'Şimdi sana bir dizi göstereceğim. İyi bak, sonra kapatacağım. Sondan başa doğru söyle.'
        : 'Şimdi sana bir dizi göstereceğim. İyi bak, sonra kapatacağım. Aynı sırayla söyle.',
      render: [
        { t: 'grid', cols: len + 1, rows: rounds, cells, border: false, gap: 4 },
        {
          t: 'rows',
          items: sequences.map((_, i) => ({ label: `${i + 1}. dizi`, blank: true, id: 'q' + i })),
          // Ekranda cevap bu semboller arasından dokunarak veriliyor.
          // Kağıtta palet yok sayılır; çocuk doğrudan çizer/yazar.
          palette: useShapes
            ? SHAPES_SIMPLE.map((s) => ({ shape: s }))
            : Array.from({ length: 9 }, (_, i) => ({ glyph: String(i + 1) })),
          paletteMode: 'sequence',
        },
      ],
      solution: sequences.map((s) => (reversed ? [...s].reverse() : s).join(' ')),
      grading: 'auto',
      estimatedSeconds: rounds * (len * 2 + 10),
      itemCount: rounds,
    }
  },
  grade(instance, answer) {
    return gradeList(instance.solution as string[], answer)
  },
}

/* ================================================================== *
 * 7. Eşleştirme belleği (yalnızca dijital)
 *
 * Kartlar kapalı, çiftleri bul. Kağıtta anlamı yok, o yüzden
 * channels yalnızca digital — program motoru kağıt haftasına bunu
 * koymasın diye.
 * ================================================================== */
export const memoryPairs: ExerciseTemplate = {
  code: 'memory_pairs',
  title: 'Çiftleri bul',
  domains: ['working_memory_visual'],
  channels: ['digital'],
  params(level) {
    const l = clampLevel(level)
    return { pairs: scale(l, 4, 12), peekMs: scale(l, 3000, 800) }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const pairs = p['pairs'] as number
    const pool = [...SHAPES_SIMPLE, ...LETTERS].slice(0, Math.max(pairs, 6))
    const picked = rng.sample(pool, pairs)
    const deck = rng.shuffle([...picked, ...picked])

    const cells: CellNode[] = deck.map((g, i) => ({ ...isaret(g), id: 'k' + i }))
    const cols = pairs <= 6 ? 4 : 6

    return {
      code: 'memory_pairs',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: 'Kartları çevirerek aynı olan çiftleri bul.',
      render: [{ t: 'grid', cols, rows: Math.ceil(deck.length / cols), cells, border: true }],
      solution: deck,
      grading: 'auto',
      estimatedSeconds: pairs * 12,
      itemCount: pairs,
    }
  },
  grade(instance, answer) {
    // Cevap: bulunan çiftlerin sembolleri
    const deck = instance.solution as string[]
    const want = Array.from(new Set(deck))
    return gradeSet(want, answer)
  },
}

/* ================================================================== *
 * 8. Sembol kodlama
 *
 * Anahtarda sembol–sayı eşleşmesi var; satırdaki sembollerin
 * karşılığını yaz. Klasik işlem hızı görevi, ama burada anahtar
 * ezberlenebilir olduğu için bellek de işin içinde.
 * ================================================================== */
export const symbolCode: ExerciseTemplate = {
  code: 'symbol_code',
  title: 'Şifreyi çöz',
  domains: ['processing_speed', 'working_memory_visual'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return { keySize: scale(l, 4, 9), items: scale(l, 12, 40) }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const keySize = p['keySize'] as number
    const count = p['items'] as number

    /**
     * Havuz TEKİLLEŞTİRİLİYOR.
     *
     * Eskiden `SHAPES_SIMPLE.concat(['◆', '◇', '□', '△'])` yazılıydı ve
     * `◆` iki listede de vardı. `sample` girdileri değil ÖĞELERİ seçtiği
     * için anahtara aynı sembol iki kez, iki farklı sayıyla girebiliyordu:
     * 10. düzeyde 300 sayfanın 258'inde oluyordu ve ikiz sembol her
     * seferinde sorulardan birinde geçiyordu. Çocuk iki doğru cevaptan
     * birini yazıyor, yarısında yanlış sayılıyordu.
     *
     * Katman 0'dan sonra havuz iki ADLI kümeden kuruluyor
     * (`SHAPES_SIMPLE` + içi boş karşılıkları) ve ikisinin kesişimi boş;
     * tekilleştirme yine de duruyor, çünkü bu hatayı tutan şey listenin
     * o anki içeriği değil, buradaki kural olmalı.
     */
    const havuz = [...new Set<ShapeName>([...SHAPES_SIMPLE, ...SHAPES_HOLLOW])]
    const symbols = rng.sample(havuz, keySize)
    const key = symbols.map((s, i) => ({ fromShape: s, to: String(i + 1) }))
    const seq = Array.from({ length: count }, () => rng.int(0, keySize - 1))

    return {
      code: 'symbol_code',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: 'Anahtara bakarak her şeklin altına sayısını yaz.',
      render: [
        { t: 'key', pairs: key },
        {
          t: 'rows',
          items: seq.map((s, i) => ({ label: '', labelShape: symbols[s]!, blank: true, id: 'i' + i })),
          columns: 10,
        },
      ],
      solution: seq.map((s) => String(s + 1)),
      grading: 'auto',
      estimatedSeconds: count * 3,
      itemCount: count,
    }
  },
  grade(instance, answer) {
    return gradeList(instance.solution as string[], answer)
  },
}

/* ================================================================== *
 * 9. İşitsel yönerge takibi
 *
 * Çok adımlı sözlü yönerge; ses TTS ile üretilir. İşitsel işleyen
 * belleğin en doğrudan ölçüldüğü egzersiz ve GETAP'ın kağıt
 * setinde karşılığı yok.
 * ================================================================== */
export const listeningSteps: ExerciseTemplate = {
  code: 'listening_steps',
  title: 'Yönergeyi dinle ve uygula',
  domains: ['working_memory_verbal', 'sustained_attention'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return { steps: scale(l, 2, 6), gridSize: scale(l, 3, 5) }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const steps = p['steps'] as number
    const size = p['gridSize'] as number
    const n = size * size

    const cells: CellNode[] = Array.from({ length: n }, (_, i) => ({ id: 'g' + i }))
    const shapes = rng.sample(SHAPES_SIMPLE, steps)
    const slots = rng.sample(Array.from({ length: n }, (_, i) => i), steps)

    const instructions: string[] = []
    const solution: { cell: string; shape: ShapeName }[] = []
    for (let i = 0; i < steps; i++) {
      const r = Math.floor(slots[i]! / size) + 1
      const c = (slots[i]! % size) + 1
      // Yönerge SESLİ okunuyor: burada şeklin Türkçe adı geçmeli
      instructions.push(`${r}. satır ${c}. kutuya ${SEKIL_ADI[shapes[i]!]} çiz`)
      solution.push({ cell: 'g' + slots[i]!, shape: shapes[i]! })
    }

    return {
      code: 'listening_steps',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: 'Yönergeyi dinle, sonra uygula. Yönerge bir kez söylenir.',
      spokenPrompt: instructions.join(', sonra '),
      render: [{ t: 'grid', cols: size, rows: size, cells, border: true }],
      solution,
      grading: 'auto',
      estimatedSeconds: steps * 15 + 20,
      itemCount: steps,
    }
  },
  grade(instance, answer) {
    const want = instance.solution as { cell: string; shape: ShapeName }[]
    const got = (answer ?? {}) as Record<string, string>
    const perItem = want.map((w) => got[w.cell] === w.shape)
    const correct = perItem.filter(Boolean).length
    return {
      correct,
      total: want.length,
      accuracy: want.length ? correct / want.length : 0,
      perItem,
      items: want.map((w) => ({
        id: w.cell,
        // Uygulayıcı raporunda okunur olmalı: 'daire' değil 'daire' zaten
        // okunur ama içi boşlar için 'içi boş kare' gerekiyor
        beklenen: SEKIL_ADI[w.shape],
        verilen: got[w.cell] ? (SEKIL_ADI[got[w.cell] as ShapeName] ?? got[w.cell]!) : null,
        dogru: got[w.cell] === w.shape,
      })),
    }
  },
}

/* ================================================================== *
 * 10. Okuduğunu anlama + hedef kelime
 *
 * Kısa metin; belirli bir kelimenin her geçişini işaretle, sonra
 * içerik sorusuna cevap ver. Dikkat ile anlama aynı anda çalışır.
 * ================================================================== */
export const readingTarget: ExerciseTemplate = {
  code: 'reading_target',
  title: 'Oku ve işaretle',
  domains: ['sustained_attention', 'working_memory_verbal'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return { sentences: scale(l, 3, 10), targetCount: scale(l, 2, 7) }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const sentenceCount = p['sentences'] as number
    const targetCount = p['targetCount'] as number
    const target = rng.pick(WORDS_TR)
    const others = WORDS_TR.filter((w) => w !== target)

    // Hedefin kaç cümlede geçeceğini önce belirle, sonra cümleleri kur:
    // böylece çözüm sayısı ile metin her zaman tutarlı olur
    const targetSentences = new Set(
      rng.sample(Array.from({ length: sentenceCount }, (_, i) => i), Math.min(targetCount, sentenceCount)),
    )

    const sentences: string[] = []
    for (let i = 0; i < sentenceCount; i++) {
      const words = rng.sample(others, 4)
      if (targetSentences.has(i)) words.splice(rng.int(0, words.length), 0, target)
      sentences.push(words.join(' ') + '.')
    }

    const actual = sentences.reduce(
      (acc, s) => acc + s.split(/\s+/).filter((w) => w.replace('.', '') === target).length,
      0,
    )

    return {
      code: 'reading_target',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: `Metni oku ve "${target}" kelimesinin her geçtiği yeri işaretle. Sonra kaç kez geçtiğini yaz.`,
      render: [
        { t: 'text', value: sentences.join(' '), size: 'md' },
        { t: 'rows', items: [{ label: 'Kaç kez geçti?', blank: true, id: 'count' }] },
      ],
      solution: { target, count: actual },
      grading: 'auto',
      estimatedSeconds: sentenceCount * 12,
      itemCount: 1,
    }
  },
  grade(instance, answer) {
    const want = instance.solution as { target: string; count: number }
    const ham = (answer as { count?: unknown })?.count ?? answer
    return gradeSingle(want.count, ham, want.target)
  },
}
